<script setup>
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, ref, toRefs, watch } from 'vue'
import { useRoute } from 'vue-router'
import { DriverStore } from '../pinia/Driver.js'

const props = defineProps({
  data: Object,
  crewmanage: Boolean,
  request: Boolean,
  change: Boolean,
  changebis: Boolean,
  event: String,
  statusBarOnly: { type: Boolean, default: false },
  isCrewOwner: { type: Boolean, default: false },
  crewOwnerId: { type: [String, Number], default: null },
})
const emit = defineEmits(['refreshmembers', 'closeDialog'])
const { data, crewmanage, request, event, change, changebis, statusBarOnly } = toRefs(props)
const { proxy } = getCurrentInstance()
const Driver = DriverStore()
const route = useRoute()

const dialog = ref(false)
const driverToDelete = ref(null)
const errorMessage = ref('')
const deleting = ref(false)
const statusHost = ref(null)
const statusText = ref(null)
const scrolling = ref(false)
const shift = ref(0)
const duration = ref(6)
let observer = null

const personaId = computed(() => data.value?.personaID ?? data.value?.personaid ?? data.value?.ID ?? data.value?.id)
const driverName = computed(() => data.value?.name || data.value?.pseudo || data.value?.nickname || '')
const avatarSrc = computed(() => {
  const index = data.value?.iconIndex ?? data.value?.iconindex ?? 0
  return `https://cdn.nightriderz.world/images/website/icon-persona/${Number.isFinite(Number(index)) ? Number(index) : 0}.webp`
})
const isAdminMember = computed(() => {
  if (data.value?.canManage == '1') return true
  const owner = props.crewOwnerId
  if (owner == null || owner === '') return false
  return String(personaId.value) === String(owner)
})
const canToggleAdmin = computed(() => !!(
  crewmanage.value
  && props.isCrewOwner
  && String(personaId.value) !== String(Driver.persona.ID)
  && !(props.crewOwnerId != null && String(personaId.value) === String(props.crewOwnerId))
))
const canKickBan = computed(() => {
  if (!crewmanage.value || String(personaId.value) === String(Driver.persona.ID)) return false
  return isAdminMember.value ? props.isCrewOwner : true
})
const hasManageActions = computed(() => canToggleAdmin.value || canKickBan.value)
const presence = computed(() => {
  const status = String(data.value?.xmpp || '').trim().toUpperCase()
  if (!status) return 'offline'
  return status.includes('RACING') ? 'racing' : 'online'
})
const showLevel = computed(() => Boolean(data.value?.level))
const showRole = computed(() => data.value?.role && data.value.role !== 'FREEMIUM')
const iconOnlyRole = computed(() => ['PREMIUM', 'STAFF', 'CONTENT_CREATOR'].includes(data.value?.role))
const cardClass = computed(() => ({
  'with-reward': data.value?.reward == 1,
  'current-driver': changebis.value,
  'is-inactive': !!data.value?.is_inactive,
  'has-manage-actions': hasManageActions.value,
  [`presence-${presence.value}`]: true,
  [`role-${(data.value?.role || 'FREEMIUM').toLowerCase()}`]: !!data.value?.role,
}))
function formatCreated(value) {
  const raw = String(value || '').trim()
  const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/)
  if (iso) return iso[1]
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return raw
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}
const meta = computed(() => {
  const rows = []
  const format = proxy.number_format.bind(proxy)
  if (data.value?.xmpp && !statusBarOnly.value && route.name !== 'Crew' && route.name !== 'Community Event') {
    rows.push({ type: 'status', text: data.value.xmpp, class: 'status-online' })
  }
  if (data.value?.date_join) rows.push({ type: 'joined', icon: 'mdi-clock-outline', datetime: data.value.date_join })
  const createdAt = data.value?.first_login || data.value?.created
  if ((change.value || changebis.value) && createdAt) rows.push({ type: 'created', icon: 'mdi-calendar', text: formatCreated(createdAt) })
  const repValue = Number(data.value?.rep ?? data.value?.points)
  if (Number.isFinite(repValue) && repValue > 0) rows.push({ type: 'points', text: `${format(repValue)} rep` })
  if (data.value?.contributor) rows.push({ type: 'contributor', icon: 'mdi-trophy', text: `${format(data.value.contributor)} ${event.value || ''}`.trim() })
  return rows
})

async function measureStatus() {
  await nextTick()
  const host = statusHost.value
  const text = statusText.value
  if (!(host instanceof HTMLElement) || !(text instanceof HTMLElement)) {
    scrolling.value = false
    shift.value = 0
    return
  }
  const overflow = text.scrollWidth - host.clientWidth
  scrolling.value = overflow > 1
  shift.value = scrolling.value ? -overflow : 0
  duration.value = scrolling.value ? Math.max(4.5, Math.min(14, 3 + overflow / 40)) : 6
}
function bindStatusHost(el) {
  observer?.disconnect()
  observer = null
  statusHost.value = el || null
  if (el instanceof HTMLElement) {
    observer = new ResizeObserver(() => measureStatus())
    observer.observe(el)
    measureStatus()
  }
}
function bindStatusText(el) {
  statusText.value = el || null
  measureStatus()
}
onBeforeUnmount(() => observer?.disconnect())
watch(() => data.value?.xmpp, measureStatus)
watch(scrolling, async (value) => { if (value) await measureStatus() })

async function action(type) {
  await proxy.$api({ serviceName: 'crew', methodName: type, parameters: [route.params.id, personaId.value, ''] })
  emit('refreshmembers')
}
async function accept() {
  await proxy.$api({ serviceName: 'crew', methodName: 'acceptjoin', parameters: [personaId.value, route.params.id] })
  emit('refreshmembers')
}
async function reject() {
  await proxy.$api({ serviceName: 'crew', methodName: 'rejectjoin', parameters: [personaId.value, route.params.id] })
  emit('refreshmembers')
}
async function admin() {
  const value = data.value.canManage == '0' ? '1' : '0'
  await proxy.$api({ serviceName: 'crew', methodName: 'canmanage', parameters: [route.params.id, personaId.value, value] })
  emit('refreshmembers')
}
function changePersona() {
  Driver.setEasharpptr_p(data.value.ID)
  getUserInfo()
  emit('closeDialog')
}
async function getUserInfo() {
  const result = await proxy.$api({ serviceName: 'session', methodName: 'GetUserInfo' })
  Driver.setPersona(result.persona)
  Driver.setUser(result.user)
  Driver.setCrew(result.crew)
  Driver.setCurrentCrew(result.crew.current)
  Driver.setOthers(result.others)
  Driver.setLogged(true)
}
function confirmDelete(id) {
  driverToDelete.value = id
  errorMessage.value = ''
  deleting.value = false
  dialog.value = true
}
async function deleteDriver() {
  if (!driverToDelete.value || deleting.value) return
  deleting.value = true
  try {
    const result = await proxy.$api({ serviceName: 'account', methodName: 'deletePersona', parameters: [driverToDelete.value] })
    if (result === 'Persona deleted successfully.') {
      await getUserInfo()
      dialog.value = false
    } else errorMessage.value = result
  } catch (error) {
    errorMessage.value = error.response?.data || 'An error occurred while deleting the driver.'
  } finally {
    deleting.value = false
  }
}
function roleIcon(role) {
  return { STAFF: 'mdi-shield-account', PREMIUM: 'mdi-crown', DEVELOPER: 'mdi-wrench', CONTENT_CREATOR: 'mdi-star' }[role] || 'mdi-account'
}
</script>

<template><div v-if="data" class="driver-card-wrapper">
    <v-dialog v-model="dialog" width="480" persistent scrim="rgba(0,0,0,0.72)" transition="dialog-bottom-transition" :z-index="2600">
      <v-card class="delete-dialog-card">
        <header class="delete-header">
          <div class="delete-header-main">
            <div class="delete-icon">
              <v-icon size="22">mdi-alert-circle</v-icon>
            </div>
            <div class="delete-heading">
              <span class="delete-kicker">Driver</span>
              <h2>Delete driver</h2>
            </div>
          </div>
          <button type="button" class="delete-close" :disabled="deleting" aria-label="Close" @click="dialog = false">
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </header>
        <p class="delete-copy">
          <template v-if="errorMessage">{{ errorMessage }}</template>
          <template v-else>Are you sure you want to delete <strong>{{ driverName }}</strong>? This action cannot be undone.</template>
        </p>
        <div class="delete-actions">
          <v-btn variant="outlined" :disabled="deleting" @click="dialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="deleting" :disabled="deleting" @click="deleteDriver">Delete</v-btn>
        </div>
      </v-card>
    </v-dialog>
    <router-link :to="'/driver/' + driverName" class="driver-card" :class="cardClass">
      <div class="driver-accent" :class="'presence-' + presence"></div>
      <div class="driver-avatar">
        <img :src="avatarSrc" :alt="driverName">
        <div v-if="showLevel" class="driver-level" :class="data.role">
          <span class="level-number">{{ data.level }}</span>
          <div class="level-icon" :class="data.role" v-html="levelIcon(data.level)"></div>
        </div>
      </div>
      <div class="driver-main">
        <div class="driver-top">
          <h4 class="driver-name" :class="data.role">
            <span v-if="data.tag" class="driver-tag">[{{ data.tag }}]</span>
            <span class="driver-nickname" :style="data.color ? { color: data.color } : undefined">{{ driverName }}</span>
            <span v-if="showRole" class="role-chip" :class="[data.role, { 'icon-only': iconOnlyRole }]">
              <v-icon :size="iconOnlyRole ? 14 : 12">{{ roleIcon(data.role) }}</v-icon>
              <span v-if="!iconOnlyRole" class="role-label">{{ data.role.replace('_', ' ') }}</span>
            </span>
          </h4>
          <div class="driver-badges">
            <span v-if="changebis" class="current-chip">
              <v-icon size="12">mdi-check-circle</v-icon> Current
            </span>
            <span v-if="data.is_inactive" class="inactive-chip">
              <v-icon size="14">mdi-account-clock</v-icon>
            </span>
          </div>
        </div>
        <div v-if="meta.length" class="driver-meta">
          <span v-for="item in meta" :key="item.type" class="meta-item" :class="item.class">
            <span v-if="item.type === 'status'" :ref="bindStatusHost" class="status-scroll" :class="{ scrolling }" :style="scrolling ? { '--status-shift': `${shift}px`, '--status-duration': `${duration}s` } : undefined" :title="item.text">
              <span class="status-scroll-inner">
                <span :ref="bindStatusText" class="status-text">{{ item.text }}</span>
              </span>
            </span>
            <template v-else-if="item.type === 'joined'">
              <v-icon size="12">{{ item.icon }}</v-icon>
              <span class="meta-text">Joined <timeago :datetime="item.datetime" /></span>
            </template>
            <template v-else-if="item.type === 'created'">
              <v-icon size="12">{{ item.icon }}</v-icon>
              <span class="meta-text">Created {{ item.text }}</span>
            </template>
            <template v-else-if="item.type === 'points'">{{ item.text }}</template>
            <template v-else>
              <v-icon size="12">{{ item.icon }}</v-icon> {{ item.text }}
            </template>
          </span>
        </div>
      </div>
      <div v-if="hasManageActions || request || change" class="driver-actions" @click.prevent.stop>
        <div v-if="hasManageActions" class="action-buttons-stack">
          <v-btn v-if="canToggleAdmin" size="x-small" icon :color="data.canManage == '1' ? 'primary' : undefined" variant="tonal" @click="admin">
            <v-icon size="12">mdi-shield-crown-outline</v-icon>
            <v-tooltip activator="parent" location="start" :text="data.canManage == '1' ? 'Remove admin' : 'Set as admin'" />
          </v-btn>
          <v-btn v-if="canKickBan" size="x-small" icon variant="tonal" @click="action('Kick')">
            <v-icon size="12">mdi-hand-wave</v-icon>
            <v-tooltip activator="parent" location="start" text="Kick from crew" />
          </v-btn>
          <v-btn v-if="canKickBan" size="x-small" icon color="error" variant="tonal" @click="action('Ban')">
            <v-icon size="12">mdi-gavel</v-icon>
            <v-tooltip activator="parent" location="start" text="Ban from crew" />
          </v-btn>
        </div>
        <div v-if="request" class="action-buttons">
          <v-btn size="x-small" icon color="success" variant="tonal" @click="accept">
            <v-icon size="12">mdi-checkbox-marked-circle-outline</v-icon>
            <v-tooltip activator="parent" location="top" text="Accept" />
          </v-btn>
          <v-btn size="x-small" icon color="error" variant="tonal" @click="reject">
            <v-icon size="12">mdi-close-circle-outline</v-icon>
            <v-tooltip activator="parent" location="top" text="Decline" />
          </v-btn>
        </div>
        <div v-if="change" class="action-buttons">
          <v-btn size="x-small" icon color="primary" variant="tonal" @click="changePersona">
            <v-icon size="12">mdi-swap-horizontal</v-icon>
            <v-tooltip activator="parent" location="top" text="Switch to this driver" />
          </v-btn>
          <v-btn size="x-small" icon color="error" variant="tonal" @click="confirmDelete(data.ID)">
            <v-icon size="12">mdi-delete</v-icon>
            <v-tooltip activator="parent" location="top" text="Delete driver" />
          </v-btn>
        </div>
      </div>
    </router-link>
  </div></template>

<style scoped>
.driver-card-wrapper {
  width:100%}
.driver-card {
  display:grid;
  grid-template-columns:auto 1fr auto;
  align-items:center;
  gap:12px;
  padding:10px 12px;
  background:var(--hover-color);
  border-radius:10px;
  border:1px solid var(--line);
  transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease,background .18s ease;
  position:relative;
  overflow:hidden;
  width:100%;
  margin-bottom:8px;
  text-decoration:none;
  color:inherit;
  cursor:pointer}
.driver-card:hover {
  background:var(--hover-color);
  border-color:rgba(var(--v-theme-primary),.35);
  transform:translateY(-1px);
  box-shadow:0 8px 20px #00000038}
.driver-accent {
  position:absolute;
  left:0;
  top:0;
  bottom:0;
  width:3px;
  background:#9e9e9e8c;
  transition:background .18s ease}
.driver-accent.presence-offline, .driver-card.presence-offline .driver-accent {
  background:#9e9e9e8c}
.driver-accent.presence-online, .driver-card.presence-online .driver-accent {
  background:#43a047}
.driver-accent.presence-racing, .driver-card.presence-racing .driver-accent {
  background:#1e88e5}
.driver-card:hover .driver-accent.presence-offline, .driver-card.presence-offline:hover .driver-accent {
  background:#bdbdbdbf}
.driver-card:hover .driver-accent.presence-online, .driver-card.presence-online:hover .driver-accent {
  background:#66bb6a}
.driver-card:hover .driver-accent.presence-racing, .driver-card.presence-racing:hover .driver-accent {
  background:#42a5f5}
.driver-card.with-reward {
  border-color:#73ad2173;
  background:linear-gradient(90deg,#73ad2114,#ffffff05)}
.driver-card.current-driver {
  border-color:rgba(var(--v-theme-primary),.45);
  background:linear-gradient(90deg,rgba(var(--v-theme-primary),.12),#ffffff05)}
.driver-card.is-inactive {
  border-color:#e5393580;
  background:linear-gradient(90deg,#e539352e,#e539350a 42%,#ffffff05)}
.driver-card.is-inactive .driver-accent {
  background:#e53935}
.driver-avatar {
  position:relative;
  flex-shrink:0}
.driver-avatar img {
  width:48px;
  height:48px;
  border-radius:10px;
  border:2px solid rgba(255,255,255,.14);
  object-fit:cover;
  display:block}
.driver-card:hover .driver-avatar img {
  border-color:rgba(var(--v-theme-primary),.45)}
.driver-level {
  position:absolute;
  bottom:-5px;
  right:-5px;
  display:flex;
  align-items:center;
  gap:3px;
  background:#12141cf5;
  padding:2px 6px;
  border-radius:999px;
  border:1px solid var(--line-strong);
  font-size:.68rem;
  font-weight:700;
  line-height:1;
  color:#8ec7ff;
  fill:#8ec7ff}
.level-icon {
  display:flex;
  align-items:center;
  justify-content:center;
  color:inherit;
  fill:currentColor}
.level-icon svg {
  width:12px;
  height:12px;
  display:block;
  fill:currentColor;
  color:inherit}
.level-icon path, .level-icon polygon {
  fill:currentColor}
.driver-main {
  min-width:0}
.driver-top {
  display:flex;
  align-items:center;
  flex-wrap:nowrap;
  gap:8px;
  margin-bottom:4px;
  min-width:0}
.driver-name {
  margin:0;
  min-width:0;
  flex:0 1 auto;
  display:inline-flex;
  align-items:center;
  gap:6px;
  font-size:.98rem;
  font-weight:600;
  line-height:1.25;
  color:var(--ink);
  white-space:nowrap;
  overflow:hidden}
.driver-tag {
  color:var(--mute);
  font-size:inherit;
  font-weight:inherit;
  line-height:inherit;
  vertical-align:baseline;
  flex-shrink:0}
.driver-nickname {
  font-size:inherit;
  font-weight:inherit;
  line-height:inherit;
  vertical-align:baseline;
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis}
.driver-name .role-chip {
  flex-shrink:0}
.driver-badges {
  display:flex;
  align-items:center;
  flex-wrap:nowrap;
  flex-shrink:0;
  gap:6px}
.role-chip.icon-only, .inactive-chip.icon-only {
  padding:4px;
  min-width:24px;
  justify-content:center}
.role-chip, .current-chip, .inactive-chip {
  display:inline-flex;
  align-items:center;
  gap:4px;
  padding:2px 8px;
  border-radius:999px;
  font-size:.62rem;
  font-weight:700;
  letter-spacing:.04em;
  text-transform:uppercase;
  line-height:1.2}
.current-chip {
  background:rgba(var(--v-theme-primary),.16);
  color:rgb(var(--v-theme-primary));
  border:1px solid rgba(var(--v-theme-primary),.28)}
.inactive-chip {
  background:#e5393529;
  color:#ef5350;
  border:1px solid rgba(229,57,53,.35)}
.driver-meta {
  display:flex;
  flex-wrap:nowrap;
  align-items:center;
  gap:6px 10px;
  min-width:0;
  overflow:hidden}
.meta-item {
  display:inline-flex;
  align-items:center;
  gap:4px;
  font-size:.74rem;
  line-height:1;
  color:var(--mute);
  white-space:nowrap}
.meta-item :deep(.v-icon) {
  opacity:.65;
  flex:none;
  width:12px!important;
  height:12px!important;
  font-size:12px!important;
  line-height:1}
.meta-text {
  display:inline-flex;
  align-items:center;
  gap:4px;
  line-height:1}
.meta-text :deep(.timeago),
.meta-text :deep(time),
.meta-text :deep(span) {
  display:inline;
  line-height:1}
.meta-item.status-online {
  color:var(--ink);
  max-width:min(100%,200px);
  min-width:0;
  flex:1 1 120px;
  overflow:hidden}
.status-scroll {
  display:block;
  width:100%;
  max-width:100%;
  min-width:0;
  overflow:hidden}
.status-scroll-inner {
  display:inline-flex;
  width:max-content;
  white-space:nowrap;
  will-change:transform}
.status-text {
  flex-shrink:0;
  white-space:nowrap}
.status-scroll.scrolling .status-scroll-inner {
  animation:status-marquee-bounce var(--status-duration, 6s) ease-in-out infinite}
.status-scroll.scrolling:hover .status-scroll-inner {
  animation-play-state:paused}
@keyframes status-marquee-bounce {
  0%, 12% {
  transform:translate(0)}
45%, 57% {
  transform:translate(var(--status-shift, 0px))}
90%, to {
  transform:translate(0)}
}.driver-card.has-manage-actions {
  grid-template-columns:auto 1fr;
  padding-right:28px}
.driver-actions {
  flex-shrink:0}
.driver-actions:has(.action-buttons-stack) {
  position:absolute;
  top:50%;
  right:8px;
  transform:translateY(-50%);
  z-index:2}
.action-buttons {
  display:flex;
  gap:4px;
  align-items:center}
.action-buttons-stack {
  display:flex;
  flex-direction:column;
  align-items:flex-end;
  gap:1px}
.action-buttons .v-btn {
  min-width:auto!important}
.action-buttons-stack .v-btn {
  width:18px!important;
  height:18px!important;
  min-width:18px!important;
  min-height:18px!important;
  padding:0!important}
.action-buttons-stack .v-btn .v-icon {
  font-size:12px!important;
  width:12px;
  height:12px}
.PREMIUM {
  color:#d9be00;
  fill:#d9be00}
.driver-level.PREMIUM, .driver-level.PREMIUM .level-icon, .driver-level.PREMIUM .level-icon svg, .driver-level.PREMIUM .level-icon path, .driver-level.PREMIUM .level-icon polygon {
  color:#d9be00;
  fill:#d9be00}
.FREEMIUM {
  color:#0054a5;
  fill:#0054a5}
.driver-level.FREEMIUM, .driver-level.FREEMIUM .level-icon, .driver-level.FREEMIUM .level-icon svg, .driver-level.FREEMIUM .level-icon path, .driver-level.FREEMIUM .level-icon polygon {
  color:#8ec7ff;
  fill:#8ec7ff}
.STAFF {
  color:#ad4aff;
  fill:#ad4aff}
.driver-level.STAFF, .driver-level.STAFF .level-icon, .driver-level.STAFF .level-icon svg, .driver-level.STAFF .level-icon path, .driver-level.STAFF .level-icon polygon {
  color:#ad4aff;
  fill:#ad4aff}
.role-chip.STAFF {
  background:#ad4aff24;
  color:#ad4aff;
  border:1px solid rgba(173,74,255,.24)}
.DEVELOPER {
  color:#11fc4a;
  fill:#11fc4a}
.driver-level.DEVELOPER, .driver-level.DEVELOPER .level-icon, .driver-level.DEVELOPER .level-icon svg, .driver-level.DEVELOPER .level-icon path, .driver-level.DEVELOPER .level-icon polygon {
  color:#11fc4a;
  fill:#11fc4a}
.role-chip.DEVELOPER {
  background:#11fc4a1f;
  color:#11fc4a;
  border:1px solid rgba(17,252,74,.24)}
.CONTENT_CREATOR {
  color:#ff0521;
  fill:#ff0521}
.driver-level.CONTENT_CREATOR, .driver-level.CONTENT_CREATOR .level-icon, .driver-level.CONTENT_CREATOR .level-icon svg, .driver-level.CONTENT_CREATOR .level-icon path, .driver-level.CONTENT_CREATOR .level-icon polygon {
  color:#ff0521;
  fill:#ff0521}
.role-chip.CONTENT_CREATOR {
  background:#ff05211f;
  color:#ff0521;
  border:1px solid rgba(255,5,33,.24)}
.role-chip.PREMIUM {
  background:#d9be0024;
  color:#d9be00;
  border:1px solid rgba(217,190,0,.24)}
.delete-dialog-card {
  overflow:hidden;
  border-radius:16px!important;
  border:1px solid var(--line-strong);
  background:var(--card-bg-solid)!important;
  color:var(--ink);
  padding:20px 20px 18px}
.delete-header {
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px}
.delete-header-main {
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0}
.delete-icon {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:44px;
  height:44px;
  border-radius:12px;
  flex-shrink:0;
  color:var(--status-danger, #ef5350);
  background:color-mix(in srgb,var(--status-danger, #ef5350) 16%,transparent)}
.delete-heading {
  min-width:0}
.delete-kicker {
  display:block;
  margin-bottom:2px;
  font-size:.68rem;
  font-weight:800;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:var(--mute)}
.delete-heading h2 {
  margin:0;
  font-size:1.12rem;
  font-weight:800;
  letter-spacing:.02em;
  line-height:1.2;
  color:var(--ink)}
.delete-close {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px;
  height:36px;
  border-radius:10px;
  border:1px solid var(--line-strong);
  background:var(--glass);
  color:var(--ink-soft);
  cursor:pointer;
  flex-shrink:0}
.delete-close:disabled {
  opacity:.4;
  cursor:not-allowed}
.delete-close:hover:not(:disabled) {
  background:var(--glass-strong);
  color:var(--ink)}
.delete-copy {
  margin:14px 0 0;
  font-size:.88rem;
  line-height:1.45;
  color:var(--mute);
  text-align:left}
.delete-copy strong {
  color:var(--ink);
  font-weight:700}
.delete-actions {
  display:flex;
  flex-wrap:wrap;
  justify-content:flex-end;
  gap:8px;
  margin-top:18px}
@media (max-width: 640px) {
.driver-card {
  grid-template-columns:auto 1fr}
.driver-card.has-manage-actions {
  padding-right:28px}
.driver-actions:not(:has(.action-buttons-stack)) {
  grid-column:1 / -1;
  justify-self:end}
.driver-meta {
  gap:4px 8px}

}
</style>
